import { useTranslation } from "react-i18next";

/**
 * PacingTable — splits with HR target / power for a race.
 *
 * Pure presentational. Caller hands us the splits array (from
 * /athlete/race/pacing) plus the discipline string used to pick
 * the right metric chip (pace per km for runners, power for
 * cyclists, pace per 100m for swimmers).
 */

export interface PacingSplit {
  index: number;
  distance_km: number;
  cumulative_km: number;
  elapsed_seconds: number;
  segment_seconds: number;
  target_pace_per_km_s: number | null;
  target_power_watts: number | null;
  hr_low: number;
  hr_high: number;
  grade_pct: number;
  note: string | null;
}

export interface PacingTableProps {
  splits: PacingSplit[];
  discipline: "run" | "trail" | "cycle" | "swim";
  className?: string;
}

export function formatPace(secondsPerKm: number): string {
  // Display as M:SS for runners (or for /100m swimmers showing /km)
  const m = Math.floor(secondsPerKm / 60);
  const s = Math.round(secondsPerKm - m * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds - h * 3600) / 60);
  const s = Math.round(seconds - h * 3600 - m * 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function PacingTable({
  splits,
  discipline,
  className = "",
}: PacingTableProps) {
  const { t } = useTranslation("plan");

  if (splits.length === 0) {
    return (
      <div
        data-testid="pacing-table-empty"
        className={`indigo-card p-4 ${className}`}
      >
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("race.week.pacing.title", "Пейсинг")}
        </div>
        <div className="mt-2 text-xs text-fg-muted">
          {t("race.week.pacing.empty", "Укажи дистанцию и цель — соберём раскладку")}
        </div>
      </div>
    );
  }

  const isCycle = discipline === "cycle";
  const isSwim = discipline === "swim";

  return (
    <div
      data-testid="pacing-table"
      data-discipline={discipline}
      className={`indigo-card p-4 ${className}`}
    >
      <div className="flex items-baseline justify-between mb-2.5">
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("race.week.pacing.title", "Пейсинг")}
        </span>
        <span className="text-[9px] uppercase tracking-[0.18em] text-fg-muted font-bold tabular-nums">
          {t("race.week.pacing.total", "Итого {{n}}", {
            n: formatTime(splits[splits.length - 1].elapsed_seconds),
          })}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-[11.5px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-[0.16em] text-fg-muted font-bold">
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-right">{t("race.week.pacing.col_km", "км")}</th>
              <th className="px-2 py-1.5 text-right">{t("race.week.pacing.col_split", "Сплит")}</th>
              <th className="px-2 py-1.5 text-right">
                {isCycle
                  ? t("race.week.pacing.col_w", "Вт")
                  : isSwim
                  ? t("race.week.pacing.col_pace_100m", "/100м")
                  : t("race.week.pacing.col_pace", "Темп")}
              </th>
              <th className="px-2 py-1.5 text-right">{t("race.week.pacing.col_hr", "ЧСС")}</th>
              <th className="px-2 py-1.5 text-right">{t("race.week.pacing.col_grade", "Грейд")}</th>
            </tr>
          </thead>
          <tbody>
            {splits.map((s) => (
              <tr
                key={s.index}
                data-testid={`pacing-table-row-${s.index}`}
                className={`border-t border-bg-border/60 ${s.note ? "bg-bg-raised/30" : ""}`}
              >
                <td className="px-2 py-1 text-fg-muted tabular-nums">{s.index}</td>
                <td className="px-2 py-1 text-right text-fg-secondary tabular-nums">
                  {s.cumulative_km.toFixed(1)}
                </td>
                <td className="px-2 py-1 text-right text-fg-primary tabular-nums font-semibold">
                  {formatTime(s.segment_seconds)}
                </td>
                <td className="px-2 py-1 text-right text-accent tabular-nums font-semibold">
                  {isCycle && s.target_power_watts != null
                    ? `${s.target_power_watts} ${t("unit_watt", { defaultValue: "Вт" })}`
                    : s.target_pace_per_km_s != null
                    ? (isSwim
                        ? formatPace(s.target_pace_per_km_s / 10)
                        : formatPace(s.target_pace_per_km_s))
                    : "—"}
                </td>
                <td className="px-2 py-1 text-right text-fg-secondary tabular-nums">
                  {s.hr_low}–{s.hr_high}
                </td>
                {/* v4-ПРАВИЛО раздел 1.4: уклон — факт рельефа, число без семантики */}
                <td className="px-2 py-1 text-right tabular-nums text-fg-secondary">
                  {s.grade_pct > 0 ? `+${s.grade_pct.toFixed(1)}` : s.grade_pct.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
